import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useOrgData } from '../contexts/OrgDataContext';
import { useLocale } from '../contexts/LocaleContext';
import { DivisionKey } from '../types/orgchart.types';
import './EmployeesPage.css';

export const EmployeesPage: React.FC = () => {
  const {
    orgData,
    addEmployee,
    updateEmployee
  } = useOrgData();
  const [searchParams] = useSearchParams();
  const { t } = useLocale();
  const orgId = searchParams.get('org');

  // Build back link with org parameter if present
  const backLink = orgId ? `/?org=${orgId}` : '/';

  // State for new employee form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newDivision, setNewDivision] = useState<string>('');

  // State for editing employee
  const [editingEmployee, setEditingEmployee] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');

  // Deduplicate employees by name and combine their roles
  const uniqueEmployees = useMemo(() => {
    // First, get all assigned roles for each employee ID
    const rolesByEmpId: Record<string, string[]> = {};

    orgData.employees.forEach(emp => {
      rolesByEmpId[emp.id] = [];
    });

    // Go through all divisions, departments, and functions to find assignments
    Object.entries(orgData.divisions).forEach(([divKey, division]) => {
      // Check for division manager assignment via managerId on division
      if (division.managerId && rolesByEmpId[division.managerId]) {
        rolesByEmpId[division.managerId].push(t('employees.divisionManager', { name: division.name }));
      }

      division.departments?.forEach((dept, deptIdx) => {
        // Check for department manager assignment
        if (dept.managerId && rolesByEmpId[dept.managerId]) {
          rolesByEmpId[dept.managerId].push(t('employees.departmentManager', { name: dept.name }));
        }

        dept.sections?.forEach((section, sectionIdx) => {
          section.functions?.forEach((func, funcIdx) => {
            const funcId = func.id || `${divKey}-d${deptIdx}-s${sectionIdx}-f${funcIdx}`;
            const assignedEmployees = orgData.assignments?.[funcId] || [];

            assignedEmployees.forEach(empId => {
              if (rolesByEmpId[empId]) {
                rolesByEmpId[empId].push(func.name);
              }
            });
          });
        });
      });
    });

    // Check executive function assignments (CEO, Owner, C-levels)
    orgData.executiveFunctions?.forEach(execFunc => {
      const assignedEmployees = orgData.assignments?.[execFunc.id] || [];
      assignedEmployees.forEach(empId => {
        if (rolesByEmpId[empId]) {
          // Use the function name as the role
          if (!rolesByEmpId[empId].includes(execFunc.name)) {
            rolesByEmpId[empId].push(execFunc.name);
          }
        }
      });
    });

    // Also check for C-level executives who manage divisions via managesDivisions property
    orgData.employees.forEach(emp => {
      // Check managesDivisions array (C-level executives)
      if (emp.managesDivisions && emp.managesDivisions.length > 0) {
        emp.managesDivisions.forEach(divKey => {
          const division = orgData.divisions[divKey];
          if (division && rolesByEmpId[emp.id]) {
            const roleLabel = t('employees.vicePresident', { name: division.name });
            if (!rolesByEmpId[emp.id].includes(roleLabel)) {
              rolesByEmpId[emp.id].push(roleLabel);
            }
          }
        });
      }
      // Check single managesDivision property
      if (emp.managesDivision && rolesByEmpId[emp.id]) {
        const division = orgData.divisions[emp.managesDivision];
        if (division) {
          const roleLabel = t('employees.vicePresident', { name: division.name });
          if (!rolesByEmpId[emp.id].includes(roleLabel)) {
            rolesByEmpId[emp.id].push(roleLabel);
          }
        }
      }
    });

    // Group employees by name (deduplicate)
    const employeesByName: Record<string, {
      name: string;
      ids: string[];
      emails: string[];
      phones: string[];
      divNums: number[];
      roles: string[];
      active: boolean;
      primaryId: string;  // The first/main employee ID for editing
    }> = {};

    orgData.employees.forEach(emp => {
      if (!emp.name) return; // Skip employees without names

      if (!employeesByName[emp.name]) {
        employeesByName[emp.name] = {
          name: emp.name,
          ids: [],
          emails: [],
          phones: [],
          divNums: [],
          roles: [],
          active: emp.active !== false, // Default to true if not set
          primaryId: emp.id
        };
      }

      const entry = employeesByName[emp.name];
      entry.ids.push(emp.id);
      if (emp.email && !entry.emails.includes(emp.email)) {
        entry.emails.push(emp.email);
      }
      if (emp.phone && !entry.phones.includes(emp.phone)) {
        entry.phones.push(emp.phone);
      }
      if (emp.divNum !== undefined && !entry.divNums.includes(emp.divNum)) {
        entry.divNums.push(emp.divNum);
      }
      // If any employee record is inactive, mark the whole person as inactive
      if (emp.active === false) {
        entry.active = false;
      }
      // Add roles from this employee ID
      rolesByEmpId[emp.id]?.forEach(role => {
        if (!entry.roles.includes(role)) {
          entry.roles.push(role);
        }
      });
    });

    // Convert to array and sort by lowest division number, then by name
    return Object.values(employeesByName)
      .sort((a, b) => {
        const minDivA = Math.min(...a.divNums);
        const minDivB = Math.min(...b.divNums);
        if (minDivA !== minDivB) return minDivA - minDivB;
        return a.name.localeCompare(b.name, 'he');
      });
  }, [orgData, t]);

  // Get sorted divisions for dropdown
  const sortedDivisions = useMemo(() => {
    return Object.entries(orgData.divisions)
      .sort((a, b) => a[1].number - b[1].number);
  }, [orgData.divisions]);

  // Handle adding new employee
  const handleAddEmployee = () => {
    if (!newName.trim()) return;

    // Build employee params without undefined values
    const employeeParams: {
      name: string;
      division: DivisionKey;
      parentId: string;
      email?: string;
      phone?: string;
    } = {
      name: newName.trim(),
      division: (newDivision || 'hr') as DivisionKey, // Default to HR if not selected
      parentId: 'ceo'
    };

    // Only add optional fields if they have values
    if (newEmail.trim()) employeeParams.email = newEmail.trim();
    if (newPhone.trim()) employeeParams.phone = newPhone.trim();

    addEmployee(employeeParams);

    // Reset form
    setNewName('');
    setNewEmail('');
    setNewPhone('');
    setNewDivision('');
    setShowAddForm(false);
  };

  // Start editing an employee
  const handleStartEdit = (employee: typeof uniqueEmployees[0]) => {
    setEditingEmployee(employee.name);
    setEditName(employee.name);
    setEditEmail(employee.emails[0] || '');
    setEditPhone(employee.phones[0] || '');
  };

  // Save employee edits
  const handleSaveEdit = (employee: typeof uniqueEmployees[0]) => {
    // Build updates without undefined values
    const updates: { name?: string; email?: string; phone?: string } = {};
    if (editName.trim() && editName.trim() !== employee.name) {
      updates.name = editName.trim();
    }
    if (editEmail.trim()) updates.email = editEmail.trim();
    if (editPhone.trim()) updates.phone = editPhone.trim();

    // Update all employee IDs for this person
    employee.ids.forEach(id => {
      updateEmployee(id, updates);
    });
    setEditingEmployee(null);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingEmployee(null);
    setEditName('');
    setEditEmail('');
    setEditPhone('');
  };

  // Toggle employee active status
  const handleToggleActive = (employee: typeof uniqueEmployees[0]) => {
    const newActiveState = !employee.active;
    // Update all employee IDs for this person
    employee.ids.forEach(id => {
      updateEmployee(id, { active: newActiveState });
    });
  };

  return (
    <div className="employees-page">
      <header className="employees-header">
        <Link to={backLink} className="back-link">
          &rarr; {t('employees.back')}
        </Link>
        <h1>{t('employees.title')}</h1>
        <div className="employee-count">
          {t('employees.total', { count: uniqueEmployees.length })}
        </div>
        <button
          className="add-employee-btn"
          onClick={() => setShowAddForm(true)}
        >
          {t('employees.add')}
        </button>
      </header>

      {/* Add Employee Modal */}
      {showAddForm && (
        <div className="add-employee-modal-overlay" onClick={() => setShowAddForm(false)}>
          <div className="add-employee-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('employees.addNew')}</h2>
              <button className="modal-close" onClick={() => setShowAddForm(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-field">
                <label>{t('employees.name')} *</label>
                <input
                  type="text"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder={t('employees.namePlaceholder')}
                  autoFocus
                />
              </div>
              <div className="form-field">
                <label>{t('employees.email')}</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  placeholder="email@example.com"
                  dir="ltr"
                />
              </div>
              <div className="form-field">
                <label>{t('employees.phone')}</label>
                <input
                  type="tel"
                  value={newPhone}
                  onChange={e => setNewPhone(e.target.value)}
                  placeholder="050-0000000"
                  dir="ltr"
                />
              </div>
              <div className="form-field">
                <label>{t('employees.division')}</label>
                <select
                  value={newDivision}
                  onChange={e => setNewDivision(e.target.value)}
                >
                  <option value="">{t('employees.selectDivision')}</option>
                  {sortedDivisions.map(([key, div]) => (
                    <option key={key} value={key}>
                      {div.icon} {div.number}. {div.name}
                    </option>
                  ))}
                </select>
              </div>
              <p className="form-note">
                {t('employees.roleNote')}
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowAddForm(false)}>
                {t('general.cancel')}
              </button>
              <button
                className="btn-save"
                onClick={handleAddEmployee}
                disabled={!newName.trim()}
              >
                {t('employees.addEmployee')}
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="employees-content">
        <table className="employees-table">
          <thead>
            <tr>
              <th>{t('employees.number')}</th>
              <th>{t('employees.status')}</th>
              <th>{t('employees.name')}</th>
              <th>{t('employees.division')}</th>
              <th>{t('employees.email')}</th>
              <th>{t('employees.phone')}</th>
              <th>{t('employees.assignedRoles')}</th>
              <th>{t('employees.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {uniqueEmployees.map((employee, index) => {
              // Get divisions for this employee
              const divisions = employee.divNums
                .map(divNum => Object.values(orgData.divisions).find(d => d.number === divNum))
                .filter(Boolean);
              const isEditing = editingEmployee === employee.name;

              return (
                <tr key={employee.name} className={!employee.active ? 'inactive-row' : ''}>
                  <td className="row-number">{index + 1}</td>
                  <td className="employee-status">
                    <button
                      className={`status-toggle ${employee.active ? 'active' : 'inactive'}`}
                      onClick={() => handleToggleActive(employee)}
                      title={employee.active ? t('employees.markInactive') : t('employees.markActive')}
                    >
                      {employee.active ? '✓' : '✗'}
                    </button>
                  </td>
                  <td className="employee-name">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        placeholder={t('employees.namePlaceholder')}
                        className="edit-input"
                        autoFocus
                      />
                    ) : (
                      employee.name
                    )}
                  </td>
                  <td className="employee-division">
                    {divisions.length > 0 ? (
                      <div className="divisions-list">
                        {divisions.map((division, idx) => (
                          <span
                            key={idx}
                            className="division-badge"
                            style={{ backgroundColor: division!.color }}
                          >
                            {division!.icon} {division!.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="employee-email">
                    {isEditing ? (
                      <input
                        type="email"
                        value={editEmail}
                        onChange={e => setEditEmail(e.target.value)}
                        placeholder="email@example.com"
                        dir="ltr"
                        className="edit-input"
                      />
                    ) : (
                      employee.emails.length > 0 ? (
                        employee.emails.map((email, idx) => (
                          <a key={idx} href={`mailto:${email}`}>{email}</a>
                        ))
                      ) : (
                        '-'
                      )
                    )}
                  </td>
                  <td className="employee-phone">
                    {isEditing ? (
                      <input
                        type="tel"
                        value={editPhone}
                        onChange={e => setEditPhone(e.target.value)}
                        placeholder="050-0000000"
                        dir="ltr"
                        className="edit-input"
                      />
                    ) : (
                      employee.phones.length > 0 ? (
                        employee.phones.map((phone, idx) => (
                          <a key={idx} href={`tel:${phone}`}>{phone}</a>
                        ))
                      ) : (
                        '-'
                      )
                    )}
                  </td>
                  <td className="employee-roles">
                    {employee.roles.length > 0 ? (
                      <div className="roles-list">
                        {employee.roles.map((role, idx) => (
                          <span key={idx} className="role-badge">
                            {role}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="no-roles">-</span>
                    )}
                  </td>
                  <td className="employee-actions">
                    {isEditing ? (
                      <>
                        <button className="action-btn save-btn" onClick={() => handleSaveEdit(employee)} title={t('general.save')}>
                          ✓
                        </button>
                        <button className="action-btn cancel-btn" onClick={handleCancelEdit} title={t('general.cancel')}>
                          ✗
                        </button>
                      </>
                    ) : (
                      <button className="action-btn edit-btn" onClick={() => handleStartEdit(employee)} title={t('general.edit')}>
                        ✏️
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </main>
    </div>
  );
};
